import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Shield, Search, Filter, CheckCircle, XCircle, Clock, 
  DollarSign, User, Eye, RefreshCw, FileText, Calendar,
  Download, AlertTriangle
} from 'lucide-react';

const AdminLSPVerifications = () => {
  const [verifications, setVerifications] = useState([]);
  const [filteredVerifications, setFilteredVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [lspUsers, setLspUsers] = useState({});

  useEffect(() => {
    fetchVerifications();
  }, []);

  useEffect(() => {
    filterVerifications();
  }, [verifications, searchTerm, statusFilter]);

  const fetchVerifications = async () => {
    try {
      const response = await adminAPI.getAllVerifications();
      setVerifications(response.data);
      
      // Fetch LSP user details for each verification
      const userPromises = response.data.map(async (v) => {
        try {
          // We'll show LSP ID for now since we don't have a direct endpoint
          return { [v.lsp_id]: { name: `LSP ${v.lsp_id.slice(0, 8)}...`, id: v.lsp_id } };
        } catch {
          return { [v.lsp_id]: { name: 'Unknown', id: v.lsp_id } };
        }
      });
      
      const users = await Promise.all(userPromises);
      const usersMap = users.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setLspUsers(usersMap);
    } catch (error) {
      toast.error('Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  const filterVerifications = () => {
    let filtered = [...verifications];
    
    if (searchTerm) {
      filtered = filtered.filter(v => 
        v.lsp_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(v => v.verification_status === statusFilter);
    }
    
    setFilteredVerifications(filtered);
  };

  const updateVerificationStatus = async (lspId, newStatus) => {
    setUpdating(true);
    try {
      await adminAPI.updateVerificationStatus(lspId, newStatus);
      toast.success(`Verification ${newStatus}`);
      fetchVerifications();
      setSelectedVerification(null);
    } catch (error) {
      toast.error('Failed to update verification status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
      approved: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout title="LSP Verifications">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="LSP Verifications">
      <div className="space-y-6 animate-slide-in-up" data-testid="admin-lsp-verifications">
        {/* Filters */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by LSP ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="verification-search"
                />
              </div>
              <div className="w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchVerifications} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Total LSPs</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{verifications.length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-600">{verifications.filter(v => v.verification_status === 'pending').length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Approved</p>
              <p className="text-2xl font-bold text-green-600">{verifications.filter(v => v.verification_status === 'approved').length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{verifications.filter(v => v.verification_status === 'rejected').length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Verifications List */}
        <div className="space-y-4">
          {filteredVerifications.length === 0 ? (
            <Card className="glass-effect border-slate-200 dark:border-slate-700">
              <CardContent className="py-12 text-center">
                <Shield className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 dark:text-slate-400">No verifications found</p>
              </CardContent>
            </Card>
          ) : (
            filteredVerifications.map((verification) => (
              <Card 
                key={verification.id} 
                className="glass-effect border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all"
                data-testid={`verification-card-${verification.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 gradient-lsp rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            {verification.lsp_name || 'Unknown LSP'}
                          </h3>
                          <p className="text-sm text-slate-500">{verification.lsp_email || verification.lsp_id.slice(0, 12) + '...'}</p>
                        </div>
                        {getStatusBadge(verification.verification_status)}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3">
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <DollarSign className="w-4 h-4" />
                          Balance: {verification.statement_balance ? `$${verification.statement_balance.toLocaleString()}` : 'Not submitted'}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <Calendar className="w-4 h-4" />
                          Statement: {verification.statement_date ? new Date(verification.statement_date).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <FileText className="w-4 h-4" />
                          {verification.bank_statement_file_id ? 'Document Uploaded' : 'No Document'}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <Shield className="w-4 h-4" />
                          Plaid: {verification.plaid_connected ? 'Connected' : 'Not Connected'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedVerification(selectedVerification?.id === verification.id ? null : verification)}
                        data-testid={`view-verification-${verification.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {selectedVerification?.id === verification.id ? 'Hide' : 'Review'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded Review Panel */}
                  {selectedVerification?.id === verification.id && (
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 animate-slide-in-up">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">LSP Details</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Name:</span> <span className="text-slate-900 dark:text-slate-100 font-medium">{verification.lsp_name || 'Unknown'}</span></p>
                            <p><span className="text-slate-500">Email:</span> <span className="text-slate-900 dark:text-slate-100">{verification.lsp_email || 'N/A'}</span></p>
                            <p><span className="text-slate-500">Phone:</span> <span className="text-slate-900 dark:text-slate-100">{verification.lsp_phone || 'N/A'}</span></p>
                            <p><span className="text-slate-500">LSP ID:</span> <span className="text-slate-900 dark:text-slate-100 font-mono text-xs">{verification.lsp_id}</span></p>
                          </div>
                          
                          <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3 mt-4">Verification Details</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Statement Date:</span> <span className="text-slate-900 dark:text-slate-100">{verification.statement_date ? new Date(verification.statement_date).toLocaleDateString() : 'Not provided'}</span></p>
                            <p><span className="text-slate-500">Account Balance:</span> <span className="text-green-600 font-medium">{verification.statement_balance ? `$${verification.statement_balance.toLocaleString()}` : 'Not provided'}</span></p>
                            <p><span className="text-slate-500">Bank Statement:</span> <span className="text-slate-900 dark:text-slate-100">{verification.bank_statement_file_id ? 'Uploaded' : 'Not uploaded'}</span></p>
                            <p><span className="text-slate-500">Plaid Connected:</span> <span className="text-slate-900 dark:text-slate-100">{verification.plaid_connected ? 'Yes' : 'No'}</span></p>
                            {verification.plaid_verified_liquidity_amount && (
                              <p><span className="text-slate-500">Plaid Verified Amount:</span> <span className="text-green-600 font-medium">${verification.plaid_verified_liquidity_amount.toLocaleString()}</span></p>
                            )}
                            <p><span className="text-slate-500">Created:</span> <span className="text-slate-900 dark:text-slate-100">{new Date(verification.created_at).toLocaleString()}</span></p>
                          </div>
                          
                          {!verification.bank_statement_file_id && !verification.plaid_connected && (
                            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-700 dark:text-amber-400">
                                  This LSP has not uploaded any verification documents yet. Consider waiting for documentation before approving.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Update Status</h4>
                          <div className="space-y-3">
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              Current Status: {getStatusBadge(verification.verification_status)}
                            </p>
                            
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="flex-1 bg-green-600 hover:bg-green-700"
                                onClick={() => updateVerificationStatus(verification.lsp_id, 'approved')}
                                disabled={updating || verification.verification_status === 'approved'}
                                data-testid={`approve-verification-${verification.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="flex-1"
                                onClick={() => updateVerificationStatus(verification.lsp_id, 'rejected')}
                                disabled={updating || verification.verification_status === 'rejected'}
                                data-testid={`reject-verification-${verification.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                            
                            {verification.verification_status !== 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={() => updateVerificationStatus(verification.lsp_id, 'pending')}
                                disabled={updating}
                              >
                                <Clock className="w-4 h-4 mr-1" />
                                Reset to Pending
                              </Button>
                            )}
                          </div>
                          
                          <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                            <p className="text-sm text-purple-700 dark:text-purple-400">
                              <strong>Note:</strong> Approving this LSP will grant them access to the deal marketplace where they can commit to supporting deals.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminLSPVerifications;
